import React from 'react';
import {
  WrenchIcon,
  ToolboxIcon,
  ToolsIcon,
  LanguageIcon,
  ClockIcon,
  UserHardHatIcon,
} from 'components/icons';
import { Document } from 'components/document';
import { JobsiteAnnouncement } from 'apollo/generated/client-operations';
import { InputFile } from 'types';
import { isServerFile } from 'utils';
import { classes, RecipientCountsProps } from './constants';
import { AnnouncementTypeBadge } from './AnnouncementTypeBadge';
import { AttributeDescription } from './AttributeDescription';
import { RecipientCounts } from './RecipientCounts';
import {
  getSiteAccessStatusDescription,
  selectedContractorsList,
  selectedTradesList,
  selectedTitlesList,
  selectedLanguagesList,
} from './utils';

export type AnnouncementForReview = Partial<
  Omit<JobsiteAnnouncement, 'files'> & {
    files: InputFile[];
  }
>;

export const attachmentClasses = {
  container:
    'odin-border odin-border-gray-200 odin-shadow-sm odin-rounded-lg odin-p-6 odin-space-y-2',
  fileInfo: 'odin-text-xs odin-text-gray-600',
};

export function AnnouncementCard({
  announcement,
  counts,
}: {
  announcement: AnnouncementForReview;
  counts?: RecipientCountsProps['counts'];
}): React.ReactElement {
  const { subject, message } = announcement;
  return (
    <div className={classes.announcementCard}>
      <div>
        <AnnouncementTypeBadge announcement={announcement} />
      </div>
      <div className="odin-mt-2 odin-ml-1">
        <div className={classes.section}>
          <AttributeDescription
            label={subject}
            value={message}
            valueClassName="odin-whitespace-pre-wrap odin-w-full"
          />
        </div>
      </div>
      <hr className={classes.hbar} />

      <AttributeDescription
        icon={ToolboxIcon}
        label="Jobsite(s)"
        values={announcement.jobsites?.edges?.map((edge) => edge?.node?.name)}
      />
      <AttributeDescription
        icon={WrenchIcon}
        label="Contractor(s)"
        values={selectedContractorsList(announcement)}
      />
      <AttributeDescription
        icon={ToolsIcon}
        label="Trade(s)"
        values={selectedTradesList(announcement)}
      />
      <AttributeDescription
        icon={UserHardHatIcon}
        label="Title(s)"
        values={selectedTitlesList(announcement)}
      />
      <AttributeDescription
        icon={LanguageIcon}
        label="Language(s)"
        values={selectedLanguagesList(announcement)}
      />
      <AttributeDescription
        icon={ClockIcon}
        label="Site Access Status"
        value={getSiteAccessStatusDescription(announcement)}
      />
      <hr className={classes.hbar} />
      <RecipientCounts counts={counts} announcement={announcement} />
      <AttributeDescription
        label="Attachment(s)"
        valueClassName="odin-w-full"
        value={
          <div className="odin-flex odin-flex-col md:odin-grid odin-grid-cols-2 odin-gap-3">
            {announcement.files?.map((file: InputFile, index: number) => {
              const fileKey = isServerFile(file)
                ? file.fileId
                : `${file.name}-${index}`;
              return (
                <div className={attachmentClasses.container} key={fileKey}>
                  <Document file={file} />
                  <div className={attachmentClasses.fileInfo}>
                    {isServerFile(file)
                      ? `FileId: ${file.fileId}`
                      : `File name: ${file.name}`}
                  </div>
                </div>
              );
            })}
          </div>
        }
      />
    </div>
  );
}
